/**
 * DashboardFilterSync — Phase 11.
 *
 * Replaces the legacy `loadDashboardFilterData` effect that lived inside
 * FintechContextProvider. Reads all 7 domain providers' statuses + parsed
 * data, runs the cross-module `getDashboardFilters()` when all are finished,
 * and writes the result into SharedFiltersProvider.
 *
 * Must render INSIDE all 7 domain providers + SharedFiltersProvider.
 * Renders nothing.
 *
 * NOTE: This component intentionally subscribes to all 8 domain contexts to
 * detect when every dataset has loaded. Because any state change in any of
 * the 8 providers re-renders this component, it MUST remain a `() => null`
 * leaf with no children — adding children would reintroduce the re-render
 * cascade this refactor was designed to eliminate.
 */
import { useEffect } from 'react';
import { getDashboardFilters } from '../../FinTechOpsDataProcessingFunctions';
import { returnManagerRosterData } from '../../user-metrics/UserMetricsDataProcessing';
import { useCostMetrics } from './CostMetricsProvider';
import { useDogma } from './DogmaProvider';
import { usePolicyEngine } from './PolicyEngineProvider';
import { useRiskRemediator } from './RiskRemediatorProvider';
import { useSas } from './SasProvider';
import { useSharedFilters } from './SharedFiltersProvider';
import { useTickets } from './TicketsProvider';
import { useUserMetrics } from './UserMetricsProvider';

export const DashboardFilterSync = (): null => {
  const tickets = useTickets();
  const sas = useSas();
  const um = useUserMetrics();
  const pe = usePolicyEngine();
  const dogma = useDogma();
  const cost = useCostMetrics();
  const rr = useRiskRemediator();
  const { setDashboardFilterStatus, setDashboardFilterDataCalculationStatus, setDashboardFilterData } = useSharedFilters();

  const allFinished =
    tickets.finTechOpsTicketDataStatus === 'finished' &&
    sas.finTechOpsSasDataStatus === 'finished' &&
    um.finTechOpsUserMetricsDataStatus === 'finished' &&
    pe.finTechOpsPolicyEngineDataStatus === 'finished' &&
    dogma.finTechOpsDogmaDataStatus === 'finished' &&
    cost.finTechOpsCostMetricsDataStatus === 'finished' &&
    rr.finTechOpsRiskRemediatorStatus === 'finished';

  const anyError =
    tickets.finTechOpsTicketDataStatus === 'error' ||
    sas.finTechOpsSasDataStatus === 'error' ||
    um.finTechOpsUserMetricsDataStatus === 'error' ||
    pe.finTechOpsPolicyEngineDataStatus === 'error' ||
    dogma.finTechOpsDogmaDataStatus === 'error' ||
    cost.finTechOpsCostMetricsDataStatus === 'error' ||
    rr.finTechOpsRiskRemediatorStatus === 'error';

  useEffect(() => {
    if (allFinished) {
      setDashboardFilterStatus('loading');
      setDashboardFilterDataCalculationStatus('loading');
      // Exclude Manager Roster records from dashboard — roster data is only needed for the User Metrics hierarchy views
      const [, nonRosterData] = returnManagerRosterData(um.userMetricsWithUserData.data || []);
      const fv = getDashboardFilters(
        tickets.finTechOpsTicketsData.data,
        nonRosterData,
        sas.sasData.data,
        pe.finTechOpsPolicyEngineParsedData.data,
        dogma.finTechOpsDogmaParsedData.data,
        cost.finTechOpsCostMetricsParsedData.data
      );
      setDashboardFilterData({
        team_name: fv.uniqueTeams,
        managers: fv.uniqueManagers,
        assignees: fv.uniqueAssignees
      });
      setDashboardFilterStatus('finished');
      setDashboardFilterDataCalculationStatus('finished');
    } else if (anyError) {
      setDashboardFilterStatus('error');
      setDashboardFilterDataCalculationStatus('error');
    }
  }, [
    allFinished,
    anyError,
    setDashboardFilterStatus,
    setDashboardFilterDataCalculationStatus,
    setDashboardFilterData,
    tickets.finTechOpsTicketsData.data,
    sas.sasData.data,
    um.userMetricsWithUserData.data,
    pe.finTechOpsPolicyEngineParsedData.data,
    dogma.finTechOpsDogmaParsedData.data,
    cost.finTechOpsCostMetricsParsedData.data
  ]);

  return null;
};
